import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldStarIcon, StarIcon } from "@phosphor-icons/react";

const BAND_STYLES = {
  excellent: "from-emerald-400 to-emerald-600 text-white",
  great: "from-[#1D78AB] to-[#0F3C57] text-white",
  good: "from-sky-400 to-sky-600 text-white",
  fair: "from-amber-400 to-amber-600 text-white",
  new: "from-zinc-400 to-zinc-600 text-white",
};

export default function TrustScore({ sellerId }) {
  const [score, setScore] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!sellerId) return;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get(`/sellers/${sellerId}/trust-score`),
          api.get(`/sellers/${sellerId}/review-summary`),
        ]);
        setScore(s.data);
        setSummary(r.data);
      } catch {}
    })();
  }, [sellerId]);

  if (!score) return null;

  const band = score.band || "new";
  const gradient = BAND_STYLES[band] || BAND_STYLES.new;
  const breakdown = summary?.breakdown || {};
  const total = summary?.total || 0;

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white p-5 flex flex-col lg:flex-row gap-5"
      data-testid="trust-score"
    >
      <div
        className={`flex-shrink-0 rounded-xl bg-gradient-to-br ${gradient} p-5 w-full lg:w-48 flex flex-col items-center justify-center text-center`}
        data-testid="trust-score-badge"
      >
        <ShieldStarIcon size={28} weight="fill" />
        <p className="mt-1 text-[10px] font-bold tracking-widest opacity-90">TRUST SCORE</p>
        <p className="font-display font-black text-5xl tracking-tighter leading-none mt-1">
          {score.score}
        </p>
        <p className="mt-1 text-[11px] font-bold tracking-widest uppercase opacity-90">{band}</p>
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-3">
          <StarIcon size={18} weight="fill" className="text-amber-500" />
          <span className="font-display font-black text-xl">
            {score.avg_rating ? score.avg_rating.toFixed(1) : "—"}
          </span>
          <span className="text-sm text-zinc-500">
            · {total} review{total === 1 ? "" : "s"}
          </span>
        </div>

        <div className="mt-3 space-y-1">
          {[5, 4, 3, 2, 1].map((s) => {
            const c = breakdown[String(s)] || 0;
            const pct = total ? Math.round((c / total) * 100) : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-xs">
                <span className="w-5 text-zinc-500">{s}★</span>
                <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-8 text-right text-zinc-500">{c}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <Cmp label="Volume" v={score.components?.volume} max={40} />
          <Cmp label="Rating" v={score.components?.rating} max={30} />
          <Cmp label="On-time" v={score.components?.on_time} max={20} />
          <Cmp label="Verified" v={score.components?.verification} max={10} />
        </div>
      </div>
    </div>
  );
}

function Cmp({ label, v, max }) {
  const pct = Math.min(100, Math.round(((v || 0) / max) * 100));
  return (
    <div className="rounded-lg border border-zinc-200 p-2">
      <p className="text-[10px] font-bold tracking-widest text-zinc-500">
        {label}
      </p>
      <p className="font-display font-black text-base mt-0.5">
        {Math.round(v || 0)}
        <span className="text-[10px] text-zinc-400">/{max}</span>
      </p>
      <div className="mt-1 h-1 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full bg-[#1D78AB]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
